import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  HStack,
  SimpleGrid,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { DeleteIcon, EditIcon } from '@chakra-ui/icons';
import { Printer, SendHorizonal, Share2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import SharePDFButton from '../../../components/ShareQuoteButton';
import { deleteOrgQuote, getOrgQuote } from '../../../hooks/org_quotes';
import { getCompanyName, Quote } from '../../../utils/types';
import {
  QuoteField,
  QuoteHero,
  QuotePrimaryButton,
  QuoteSectionCard,
  QuoteShell,
  QuoteStatusBadge,
  formatQuoteDate,
  formatQuoteMoney,
} from '../components/quoteUi';
import ConfirmModal from '../../../components/ConfirmationModal';

export default function ViewQuote() {
  const { orgId, quoteId } = useParams<{ orgId: string; quoteId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [quote, setQuote] = useState<Quote | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      const response = await getOrgQuote(orgId, quoteId);
      if (mounted && response?.success) {
        setQuote(response.data);
      }
      if (mounted) {
        setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [orgId, quoteId]);

  const handleDelete = async () => {
    if (!orgId || !quote) {
      return;
    }

    setDeleting(true);
    const response = await deleteOrgQuote(orgId, quote.id);
    setDeleting(false);

    if (!response.success) {
      toast({
        title: 'Could not delete quotation',
        description: 'Please try again.',
        status: 'error',
        duration: 3500,
        isClosable: true,
      });
      return;
    }

    toast({
      title: 'Quotation deleted',
      description: `${quote.reference_number} was removed.`,
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
    navigate(`/organization/${orgId}/quotes`);
  };

  if (loading || !quote) {
    return (
      <QuoteShell compact>
        <QuoteSectionCard title="Loading quotation" description="Please wait while the quotation is loading.">
          <Button variant="ghost" isLoading loadingText="Loading" />
        </QuoteSectionCard>
      </QuoteShell>
    );
  }

  return (
    <QuoteShell compact>
      <QuoteHero
        eyebrow="Quotation"
        title={quote.reference_number}
        description="Review the commercial details, customer context, and next actions before you share or revise the quote."
        primaryAction={
          <QuotePrimaryButton
            leftIcon={<EditIcon />}
            onClick={() => navigate(`/organization/${orgId}/quotes/${quote.id}/edit`)}
          >
            Edit quote
          </QuotePrimaryButton>
        }
        secondaryAction={
          <HStack spacing={3}>
            <Button borderRadius="16px" variant="outline" leftIcon={<Printer size={16} />} onClick={() => window.print()}>
              Print
            </Button>
            <SharePDFButton quote={quote} />
          </HStack>
        }
        stats={[
          { label: 'Total', value: formatQuoteMoney(quote.total_amount) },
          { label: 'Status', value: quote.status?.toUpperCase() || 'DRAFT' },
          { label: 'Created', value: formatQuoteDate(quote.created_at) },
          { label: 'Valid until', value: formatQuoteDate(quote.valid_until) },
        ]}
      />

      <SimpleGrid columns={{ base: 1, xl: 3 }} spacing={6} alignItems="start">
        <Stack spacing={6} gridColumn={{ xl: 'span 2' }}>
          <QuoteSectionCard
            title="Recipient"
            description="The customer and company context attached to this quotation."
            action={<QuoteStatusBadge status={quote.status} />}
          >
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
              <QuoteField
                label="Lead"
                value={`${quote.lead?.title ? `${quote.lead.title} ` : ''}${quote.lead?.first_name || ''} ${quote.lead?.second_name || ''}`.trim() || 'Unnamed lead'}
              />
              <QuoteField label="Company" value={getCompanyName(quote.lead?.company) || 'No company'} />
              <QuoteField label="Email" value={quote.lead?.email || 'No email'} />
              <QuoteField label="Phone" value={quote.lead?.phone || 'No phone'} />
            </SimpleGrid>
          </QuoteSectionCard>

          <QuoteSectionCard
            title="Line items"
            description="The commercial composition of the quotation."
          >
            <TableContainer>
              <Table variant="simple" size="md">
                <Thead bg="gray.50">
                  <Tr>
                    <Th>Item</Th>
                    <Th isNumeric>Quantity</Th>
                    <Th isNumeric>Unit price</Th>
                    <Th isNumeric>Total</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {quote.items?.map((item) => (
                    <Tr key={item.id}>
                      <Td fontWeight="600">{item.item_name}</Td>
                      <Td isNumeric>{item.quantity}</Td>
                      <Td isNumeric>{formatQuoteMoney(item.unit_price)}</Td>
                      <Td isNumeric fontWeight="700">
                        {formatQuoteMoney(item.total_price)}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </QuoteSectionCard>

          <QuoteSectionCard
            title="Notes"
            description="Customer-facing context captured on the quotation."
          >
            <Text color="gray.700" whiteSpace="pre-wrap">
              {quote.notes || 'No notes added.'}
            </Text>
          </QuoteSectionCard>
        </Stack>

        <Stack spacing={6}>
          <QuoteSectionCard
            title="Commercial summary"
            description="High-value totals and timing in one place."
          >
            <Stack spacing={4}>
              <QuoteField label="Reference" value={quote.reference_number} />
              <QuoteField label="Status" value={<QuoteStatusBadge status={quote.status} />} />
              <QuoteField label="Created" value={formatQuoteDate(quote.created_at)} />
              <QuoteField label="Expiry" value={formatQuoteDate(quote.valid_until)} />
              <Box pt={2}>
                <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.08em" color="gray.500" mb={1}>
                  Total
                </Text>
                <Text fontSize="3xl" fontWeight="800" color="gray.900">
                  {formatQuoteMoney(quote.total_amount)}
                </Text>
              </Box>
            </Stack>
          </QuoteSectionCard>

          <QuoteSectionCard
            title="Actions"
            description="Move the quotation forward or revise it."
          >
            <VStack align="stretch" spacing={3}>
              <QuotePrimaryButton
                leftIcon={<EditIcon />}
                onClick={() => navigate(`/organization/${orgId}/quotes/${quote.id}/edit`)}
              >
                Edit quote
              </QuotePrimaryButton>
              <Button borderRadius="16px" variant="outline" leftIcon={<SendHorizonal size={16} />}>
                Ready to send
              </Button>
              <Button borderRadius="16px" variant="outline" leftIcon={<Share2 size={16} />}>
                Share PDF
              </Button>
              <Button
                borderRadius="16px"
                variant="ghost"
                colorScheme="red"
                leftIcon={<DeleteIcon />}
                onClick={onOpen}
              >
                Delete quote
              </Button>
            </VStack>
          </QuoteSectionCard>
        </Stack>
      </SimpleGrid>

      <ConfirmModal
        isOpen={isOpen}
        onClose={onClose}
        handleConfirm={handleDelete}
        isLoading={deleting}
        AreYouSureQuestion={`Delete ${quote.reference_number}?`}
        AreYouSureTextExplanation="This quotation will be permanently removed and cannot be restored."
        confirmButtonText="Delete quotation"
        confirmButtonColor="#E53E3E"
        colorScheme="red"
      />
    </QuoteShell>
  );
}
